import httpService from "../../services/http.service";
import { getCMSApiRoute, getBillingApiRoute } from "../api.utils";
import _ from "lodash";

// Cache version for invalidation when data structure changes
const CACHE_VERSION = "1.0";
const CACHE_PREFIX = "player_data_";
const TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Generate cache key for player data
 * @param {string} slug - Content slug
 * @param {Object} params - Search params (for series: s, e)
 * @returns {string}
 */
const getCacheKey = (slug, params = {}) => {
    const { s, e, isTicketing } = params;
    let key = `${CACHE_PREFIX}${slug}`;
    if (s && e) key += `_s${s}_e${e}`;
    if (isTicketing) key += "_ticket";
    return key;
};

/**
 * Get cached player data from localStorage
 * @param {string} cacheKey
 * @returns {Object|null}
 */
const getCachedData = (cacheKey) => {
    try {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        const parsed = JSON.parse(cached);

        // Validate cache version
        if (parsed.version !== CACHE_VERSION) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        // Check TTL
        if (Date.now() - parsed.timestamp > TTL_MS) {
            console.log(`[PlayerDataCache] Cache expired for ${cacheKey}`);
            localStorage.removeItem(cacheKey);
            return null;
        }

        return parsed.data;
    } catch (error) {
        console.warn(`[PlayerDataCache] Error reading cache:`, error);
        return null;
    }
};

/**
 * Save player data to localStorage cache
 * @param {string} cacheKey
 * @param {Object} data
 */
const setCachedData = (cacheKey, data) => {
    try {
        const cacheEntry = {
            data,
            timestamp: Date.now(),
            version: CACHE_VERSION,
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        console.log(`[PlayerDataCache] Cached data for ${cacheKey}`);
    } catch (error) {
        console.warn(`[PlayerDataCache] Error writing cache:`, error);
    }
};

/**
 * Fetch config from external URL
 */
const getConfigFromURL = async (configURL) => {
    try {
        const response = await fetch(configURL, {});
        const data = await response.json();
        const { source: sources, thumbnail, subTitles, subtitles, subtitle, poster } = data;
        return {
            success: true,
            sources,
            thumbnail,
            poster,
            subTitles: subTitles || subtitles || subtitle,
        };
    } catch (error) {
        console.log("[PlayerDataCache] ConfigURL fetch error:", error);
        return { success: false };
    }
};

/**
 * Fetch watch tracker data
 */
const getWatchTrackerData = async (contentKey, isEpisode, season, episode) => {
    try {
        const response = await httpService.get(
            getCMSApiRoute(
                "action/watch-tracker?" +
                new URLSearchParams(
                    _.omitBy({ contentKey, isEpisode, season, episode }, _.isNil)
                ).toString()
            )
        );
        const { data } = response.data;
        // API returns { results: [...] } structure
        return data?.results?.[0] || data?.[0] || {};
    } catch (error) {
        console.log("[PlayerDataCache] Watch tracker error:", error);
        return {};
    }
};

/**
 * Core data fetching logic (mirrors watch/page.js)
 */
const fetchPlayerData = async (slug, searchParams = {}) => {
    try {
        const decodedSlug = decodeURIComponent(slug);
        const { s, e, isTicketing, contentSlug } = searchParams;

        // Handle ticketing content
        if (Number(isTicketing) === 1) {
            const response = await httpService.get(
                getBillingApiRoute(
                    "payment?" +
                    new URLSearchParams({
                        pageNumber: 1,
                        pageIndex: 40,
                        orderType: "cinema_ticket",
                        status: 1,
                        ticketKey: decodeURI(contentSlug),
                    })
                )
            );
            const { data } = response.data;
            if (data?.results?.length !== 0) {
                const cinemaData = await httpService.get(
                    getCMSApiRoute(
                        "content/player-resource-cinema?" +
                        new URLSearchParams({
                            slug: decodedSlug,
                            ticketTraceCode: data?.results[0]?.traceCode,
                        })
                    )
                );
                return cinemaData.data?.data;
            }
            return null;
        }

        // Normal content fetch
        const response = await httpService.get(
            getCMSApiRoute(
                "content/player-resource?" +
                new URLSearchParams({ slug: decodedSlug })
            )
        );

        if (response.status !== 200) return null;

        const rawData = response.data?.data;
        if (!rawData) return null;

        // Process series/program content
        if (rawData?.contentType?.kind === "series" || rawData?.contentType?.kind === "program") {
            if (!s || !e) return null; // Need episode info to preload

            const episodeItem = rawData?.contentType.seriesInfo.find(
                (x) => +x.episodeNumber === +e && +x.seasonNumber === +s
            );

            if (!episodeItem) return null;

            const watchTrackData = await getWatchTrackerData(slug, 1, +s, +e);
            const posters = episodeItem?.data?.meta?.contentPhotos?.filter(
                (x) => x.type === "videoPlayerCover"
            );
            const hotpoints = episodeItem?.data?.meta?.hotpoints;
            let { sources, thumbnail, subTitles, configURL } = episodeItem.data;

            if (!sources && configURL) {
                const confData = await getConfigFromURL(configURL);
                if (confData.success) {
                    sources = confData.sources;
                    thumbnail = confData.thumbnail;
                    subTitles = confData.subTitles;
                }
            }

            if (!sources) return null;

            const { ads, behavior } = rawData;

            return {
                movie: { sources, thumbnail, subTitles },
                // Advertisement selection is intentionally not cached. The watch
                // route always obtains a fresh, eligibility-checked response.
                vast: null,
                hotPoints: hotpoints || [],
                ads,
                hasErr: false,
                posters,
                behavior,
                rawData,
                watchTrackData,
            };
        } else {
            // Movie content
            const hotpoints = rawData?.meta?.hotpoints;
            let { sources, ads, thumbnail, subTitles, configURL, isTicketing: rawIsTicketing, isLive, behavior } = rawData;

            if (configURL) {
                const confData = await getConfigFromURL(configURL);
                if (confData.success) {
                    sources = confData.sources;
                    thumbnail = confData.thumbnail;
                    subTitles = confData.subTitles;
                }
            }

            const watchTrackData = await getWatchTrackerData(slug);
            const posters = rawData?.contentPhotos?.filter(
                (x) => x.type === "videoPlayerCover"
            );

            return {
                movie: { sources, thumbnail, subTitles },
                // Advertisement selection is intentionally not cached. The watch
                // route always obtains a fresh, eligibility-checked response.
                vast: null,
                ads,
                posters,
                hotPoints: hotpoints || [],
                hasErr: false,
                isLive,
                isTicketing: rawIsTicketing,
                behavior,
                rawData,
                watchTrackData,
            };
        }
    } catch (error) {
        console.log("[PlayerDataCache] Fetch error:", error);
        return null;
    }
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Preload player data in background (non-blocking)
 * Call this from detail pages to warm the cache
 * 
 * @param {string} slug - Content slug
 * @param {string} contentType - Content type (movie, series, program)
 * @param {Object} searchParams - Optional params (s, e for episodes)
 */
export const preloadPlayerData = (slug, contentType, searchParams = {}) => {
    if (!slug) return;

    const cacheKey = getCacheKey(slug, searchParams);

    // Skip if already cached and valid
    if (getCachedData(cacheKey)) {
        console.log(`[PlayerDataCache] Already cached: ${cacheKey}`);
        return;
    }

    console.log(`[PlayerDataCache] Preloading: ${cacheKey}`);

    // Fire-and-forget background fetch
    fetchPlayerData(slug, searchParams)
        .then((data) => {
            if (data && !data.hasErr) {
                setCachedData(cacheKey, data);
            }
        })
        .catch((err) => {
            console.warn(`[PlayerDataCache] Preload failed:`, err);
        });
};

/**
 * Get player data from cache if available and valid
 * 
 * @param {string} slug - Content slug
 * @param {Object} searchParams - Optional params (s, e for episodes)
 * @returns {Object|null} Cached data or null
 */
export const getPlayerDataFromCache = (slug, searchParams = {}) => {
    if (!slug) return null;

    const cacheKey = getCacheKey(slug, searchParams);
    const cached = getCachedData(cacheKey);

    if (cached) {
        console.log(`[PlayerDataCache] Cache HIT: ${cacheKey}`);
        return cached;
    }

    console.log(`[PlayerDataCache] Cache MISS: ${cacheKey}`);
    return null;
};

/**
 * Check if valid cache exists for content
 * 
 * @param {string} slug - Content slug
 * @param {Object} searchParams - Optional params
 * @returns {boolean}
 */
export const isPlayerDataCached = (slug, searchParams = {}) => {
    if (!slug) return false;
    const cacheKey = getCacheKey(slug, searchParams);
    return getCachedData(cacheKey) !== null;
};

/**
 * Clear player data cache
 * 
 * @param {string} slug - Optional specific slug to clear, or all if omitted
 */
export const clearPlayerDataCache = (slug) => {
    try {
        if (slug) {
            // Clear specific content cache entries
            const keys = Object.keys(localStorage).filter((k) =>
                k.startsWith(`${CACHE_PREFIX}${slug}`)
            );
            keys.forEach((k) => localStorage.removeItem(k));
            console.log(`[PlayerDataCache] Cleared cache for ${slug}`);
        } else {
            // Clear all player cache entries
            const keys = Object.keys(localStorage).filter((k) =>
                k.startsWith(CACHE_PREFIX)
            );
            keys.forEach((k) => localStorage.removeItem(k));
            console.log(`[PlayerDataCache] Cleared all player cache`);
        }
    } catch (error) {
        console.warn(`[PlayerDataCache] Error clearing cache:`, error);
    }
};

const playerDataCache = {
    preloadPlayerData,
    getPlayerDataFromCache,
    isPlayerDataCached,
    clearPlayerDataCache,
};

export default playerDataCache;
